<template>
  <!-- ═══════════════════════════════════════════════════════════════════════
       PERSONAL MODE NAVBAR - Desktop Sidebar + Mobile Bottom Nav
  ════════════════════════════════════════════════════════════════════════ -->

  <div>
    <!-- ───────────────────────────────────────────
         DESKTOP SIDEBAR (hidden on mobile)
    ─────────────────────────────────────────────── -->
    <aside class="hidden md:flex flex-col w-64 shrink-0 h-screen sticky top-0
                  bg-white dark:bg-slate-900 border-r border-slate-100 dark:border-slate-800">

      <!-- Brand -->
      <Link href="/khata" class="flex items-center gap-3 px-5 py-5 border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition">
        <img :src="'/logos/master_ledger_1.png'" class="w-9 h-9 rounded-xl shrink-0 object-cover" alt="Master Ledger" />
        <div>
          <p class="text-sm font-bold text-slate-800 dark:text-white leading-tight">{{ businessName }}</p>
          <p class="text-xs text-slate-400">Personal Finance</p>
        </div>
      </Link>

      <!-- Nav -->
      <nav class="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        <SidebarLink v-for="item in navItems" :key="item.href"
          :href="item.href" :icon="item.icon" :label="item.label" />

        <!-- Reports collapsible group -->
        <div>
          <button @click="reportsOpen = !reportsOpen"
            class="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition"
            :class="isReportsActive
              ? 'bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400'
              : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'">
            <span class="w-8 h-8 flex items-center justify-center rounded-lg transition"
              :class="isReportsActive
                ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-400'
                : 'bg-slate-100 dark:bg-slate-800'">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 6a7.5 7.5 0 107.5 7.5h-7.5V6z"/><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 10.5H21A7.5 7.5 0 0013.5 3v7.5z"/>
              </svg>
            </span>
            <span class="flex-1 text-left">Reports</span>
            <svg class="w-3.5 h-3.5 transition-transform duration-200" :class="reportsOpen ? 'rotate-180' : ''"
              fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>

          <!-- Sub-links -->
          <div v-if="reportsOpen" class="mt-1 ml-11 space-y-0.5">
            <Link v-for="sub in reportSubItems" :key="sub.href" :href="sub.href" prefetch
              class="flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition"
              :class="page.url.startsWith(sub.href)
                ? 'bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 font-semibold'
                : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 font-medium'">
              <svg class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8" v-html="sub.icon" />
              {{ sub.label }}
            </Link>
          </div>
        </div>

        <!-- Resources collapsible group -->
        <div>
          <button @click="resourcesOpen = !resourcesOpen"
            class="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition"
            :class="isResourcesActive
              ? 'bg-teal-50 dark:bg-teal-950 text-teal-600 dark:text-teal-400'
              : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'">
            <span class="w-8 h-8 flex items-center justify-center rounded-lg transition"
              :class="isResourcesActive
                ? 'bg-teal-100 dark:bg-teal-900 text-teal-600 dark:text-teal-400'
                : 'bg-slate-100 dark:bg-slate-800'">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                <path stroke-linecap="round" stroke-linejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125"/>
              </svg>
            </span>
            <span class="flex-1 text-left">Resources</span>
            <svg class="w-3.5 h-3.5 transition-transform duration-200" :class="resourcesOpen ? 'rotate-180' : ''"
              fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>

          <!-- Sub-links -->
          <div v-if="resourcesOpen" class="mt-1 ml-11 space-y-0.5">
            <Link href="/cities" prefetch
              class="flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition"
              :class="page.url.startsWith('/cities')
                ? 'bg-teal-50 dark:bg-teal-950 text-teal-600 dark:text-teal-400 font-semibold'
                : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 font-medium'">
              <svg class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"/>
              </svg>
              Cities
            </Link>
          </div>
        </div>

        <!-- Admin collapsible group (super admin only) -->
        <div v-if="isSuperAdmin">
          <button @click="adminOpen = !adminOpen"
            class="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition"
            :class="isAdminActive
              ? 'bg-rose-50 dark:bg-rose-950 text-rose-600 dark:text-rose-400'
              : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'">
            <span class="w-8 h-8 flex items-center justify-center rounded-lg transition"
              :class="isAdminActive
                ? 'bg-rose-100 dark:bg-rose-900 text-rose-600 dark:text-rose-400'
                : 'bg-slate-100 dark:bg-slate-800'">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"/>
              </svg>
            </span>
            <span class="flex-1 text-left">Admin</span>
            <svg class="w-3.5 h-3.5 transition-transform duration-200" :class="adminOpen ? 'rotate-180' : ''"
              fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>

          <div v-if="adminOpen" class="mt-1 ml-11 space-y-0.5">
            <Link href="/admin/users" prefetch
              class="flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition"
              :class="page.url.startsWith('/admin/users')
                ? 'bg-rose-50 dark:bg-rose-950 text-rose-600 dark:text-rose-400 font-semibold'
                : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 font-medium'">
              <svg class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"/>
              </svg>
              Users
            </Link>
            <Link href="/admin/contact_us_form_users" prefetch
              class="flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition"
              :class="page.url.startsWith('/admin/contact_us_form_users')
                ? 'bg-rose-50 dark:bg-rose-950 text-rose-600 dark:text-rose-400 font-semibold'
                : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 font-medium'">
              <svg class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
                <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"/>
              </svg>
              Contact Forms
            </Link>
          </div>
        </div>
      </nav>

      <!-- Bottom actions -->
      <div class="px-3 py-4 border-t border-slate-100 dark:border-slate-800 space-y-1">
        <button @click="logout"
          class="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium
                 text-red-500 hover:bg-red-50 dark:hover:bg-red-950 transition">
          <span class="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 dark:bg-red-950">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0 3 3m-3-3h12.75"/>
            </svg>
          </span>
          Logout
        </button>

        <Link href="/settings" prefetch
          class="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition"
          :class="isSettingsActive
            ? 'bg-indigo-50 dark:bg-indigo-950'
            : 'hover:bg-slate-100 dark:hover:bg-slate-800'">
          <span class="w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center text-white text-sm font-bold shrink-0">
            {{ userInitial }}
          </span>
          <span class="flex flex-col min-w-0">
            <span class="text-xs font-semibold truncate"
              :class="isSettingsActive ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-700 dark:text-slate-300'">
              {{ userEmail }}
            </span>
            <span class="text-[10px] text-slate-400">Settings</span>
          </span>
        </Link>
      </div>
    </aside>

    <!-- ───────────────────────────────────────────
         MOBILE BOTTOM NAV
    ─────────────────────────────────────────────── -->
    <nav class="md:hidden fixed bottom-0 inset-x-0 z-20">
      <div class="bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl
                  border-t border-slate-200/60 dark:border-slate-700/60
                  shadow-[0_-4px_24px_rgba(0,0,0,0.06)] dark:shadow-[0_-4px_24px_rgba(0,0,0,0.3)]">
        <div class="flex items-stretch justify-around px-1 pt-2 pb-0.5">

          <!-- Main nav tabs -->
          <template v-for="item in mobileNavItems" :key="item.href">
            <Link :href="item.href" prefetch
              class="flex flex-col items-center gap-1 px-3 py-1 relative group flex-1 active:scale-90 transition-all duration-150">
              <span v-if="isMobileActive(item.href)"
                class="absolute top-0 left-1/2 -translate-x-1/2 w-5 h-0.5 rounded-full bg-indigo-600 dark:bg-indigo-400"></span>
              <span class="w-10 h-8 flex items-center justify-center rounded-xl transition-all duration-200 overflow-hidden relative"
                :class="isMobileActive(item.href) ? 'bg-indigo-50 dark:bg-indigo-950' : 'group-active:bg-indigo-100 dark:group-active:bg-indigo-950'">
                <span class="absolute inset-0 bg-indigo-200 dark:bg-indigo-800 opacity-0 group-active:opacity-100 group-active:scale-150 transition-all duration-300 rounded-xl"></span>
                <svg class="w-5 h-5 transition-all duration-200 relative z-10 group-active:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"
                  :class="isMobileActive(item.href) ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500 group-active:text-indigo-600 dark:group-active:text-indigo-400'"
                  v-html="item.icon" />
              </span>
              <span class="text-[10px] font-semibold transition-all duration-200"
                :class="isMobileActive(item.href) ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500 group-active:text-indigo-600 dark:group-active:text-indigo-400'">
                {{ item.label }}
              </span>
            </Link>
          </template>

          <!-- More button -->
          <button @click="moreOpen = true"
            class="flex flex-col items-center gap-1 px-3 py-1 relative flex-1 active:scale-90 transition-all duration-150 group">
            <span class="absolute top-0 left-1/2 -translate-x-1/2 w-5 h-0.5 rounded-full transition-all duration-200"
              :class="moreOpen || isMoreActive ? 'bg-indigo-600 dark:bg-indigo-400' : 'bg-transparent'"></span>
            <span class="w-10 h-8 flex items-center justify-center rounded-xl transition-all duration-200 overflow-hidden relative"
              :class="moreOpen || isMoreActive ? 'bg-indigo-50 dark:bg-indigo-950' : 'group-active:bg-indigo-100 dark:group-active:bg-indigo-950'">
              <span class="absolute inset-0 bg-indigo-200 dark:bg-indigo-800 opacity-0 group-active:opacity-100 group-active:scale-150 transition-all duration-300 rounded-xl"></span>
              <svg class="w-5 h-5 transition-all duration-200 relative z-10 group-active:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"
                :class="moreOpen || isMoreActive ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500 group-active:text-indigo-600 dark:group-active:text-indigo-400'">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM12.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM18.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"/>
              </svg>
            </span>
            <span class="text-[10px] font-semibold transition-all duration-200"
              :class="moreOpen || isMoreActive ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500 group-active:text-indigo-600 dark:group-active:text-indigo-400'">
              More
            </span>
          </button>

        </div>
      </div>
    </nav>

    <!-- "More" bottom sheet -->
    <Transition
      enter-active-class="transition-opacity duration-300"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-200"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0">
      <div v-if="moreOpen" @click="moreOpen = false"
        class="md:hidden fixed inset-0 bg-black/50 dark:bg-black/70 z-40 backdrop-blur-sm"></div>
    </Transition>

    <Transition
      enter-active-class="transition-transform duration-350 ease-[cubic-bezier(0.32,0.72,0,1)]"
      enter-from-class="translate-y-full"
      enter-to-class="translate-y-0"
      leave-active-class="transition-transform duration-250 ease-in"
      leave-from-class="translate-y-0"
      leave-to-class="translate-y-full">
      <div v-if="moreOpen"
        class="md:hidden fixed bottom-0 inset-x-0 z-50 bg-white dark:bg-slate-900
               rounded-t-3xl shadow-2xl overflow-hidden"
        style="padding-bottom: env(safe-area-inset-bottom, 0px)">

        <div class="flex justify-center pt-3 pb-1" @click="moreOpen = false">
          <div class="w-9 h-1 rounded-full bg-slate-200 dark:bg-slate-700"></div>
        </div>

        <div class="overflow-y-auto max-h-[80vh] pb-4">
          <!-- User profile row -->
          <Link href="/settings" prefetch @click="moreOpen = false"
            class="flex items-center gap-3.5 mx-4 mt-3 mb-1 p-3.5 rounded-2xl
                   bg-slate-50 dark:bg-slate-800 active:bg-slate-100 dark:active:bg-slate-700
                   transition active:scale-[0.98]">
            <span class="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600
                         flex items-center justify-center text-white text-sm font-bold shrink-0 shadow-sm">
              {{ userInitial }}
            </span>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-semibold text-slate-800 dark:text-white truncate">{{ userEmail }}</p>
              <p class="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Account Settings</p>
            </div>
            <svg class="w-4 h-4 text-slate-300 dark:text-slate-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5"/>
            </svg>
          </Link>

          <!-- Secondary Nav Items section -->
          <div class="mx-4 mt-4 mb-1">
            <p class="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest px-1 mb-2">Quick Access</p>
            <div class="grid grid-cols-3 gap-2">
              <Link v-for="item in mobileSecondaryItems" :key="item.href" :href="item.href" prefetch
                @click="moreOpen = false"
                class="flex flex-col items-center gap-2 p-3.5 rounded-2xl transition active:scale-95"
                :class="page.url.startsWith(item.href)
                  ? 'bg-indigo-50 dark:bg-indigo-950'
                  : 'bg-slate-50 dark:bg-slate-800 active:bg-slate-100 dark:active:bg-slate-700'">
                <span class="w-9 h-9 rounded-xl flex items-center justify-center"
                  :class="page.url.startsWith(item.href)
                    ? 'bg-indigo-100 dark:bg-indigo-900'
                    : 'bg-white dark:bg-slate-700 shadow-sm'">
                  <svg class="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8"
                    :class="page.url.startsWith(item.href) ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-500 dark:text-slate-400'"
                    v-html="item.icon" />
                </span>
                <span class="text-[11px] font-semibold text-center leading-tight"
                  :class="page.url.startsWith(item.href) ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-600 dark:text-slate-400'">
                  {{ item.label }}
                </span>
              </Link>
            </div>
          </div>

          <!-- Reports section -->
          <div class="mx-4 mt-4 mb-1">
            <p class="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest px-1 mb-2">Reports</p>
            <div class="grid grid-cols-3 gap-2">
              <Link v-for="sub in reportSubItems" :key="sub.href" :href="sub.href" prefetch
                @click="moreOpen = false"
                class="flex flex-col items-center gap-2 p-3.5 rounded-2xl transition active:scale-95"
                :class="page.url.startsWith(sub.href)
                  ? 'bg-indigo-50 dark:bg-indigo-950'
                  : 'bg-slate-50 dark:bg-slate-800 active:bg-slate-100 dark:active:bg-slate-700'">
                <span class="w-9 h-9 rounded-xl flex items-center justify-center"
                  :class="page.url.startsWith(sub.href)
                    ? 'bg-indigo-100 dark:bg-indigo-900'
                    : 'bg-white dark:bg-slate-700 shadow-sm'">
                  <svg class="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8"
                    :class="page.url.startsWith(sub.href) ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-500 dark:text-slate-400'"
                    v-html="sub.icon" />
                </span>
                <span class="text-[11px] font-semibold text-center leading-tight"
                  :class="page.url.startsWith(sub.href) ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-600 dark:text-slate-400'">
                  {{ sub.label }}
                </span>
              </Link>
            </div>
          </div>

          <!-- Admin section (super admin only) -->
          <template v-if="isSuperAdmin">
            <div class="mx-4 mt-4 mb-1">
              <p class="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest px-1 mb-2">Admin</p>
              <div class="grid grid-cols-2 gap-2">
                <Link href="/admin/users" prefetch @click="moreOpen = false"
                  class="flex flex-col items-center gap-2 p-3 rounded-2xl transition active:scale-[0.98]"
                  :class="page.url.startsWith('/admin/users')
                    ? 'bg-rose-50 dark:bg-rose-950'
                    : 'bg-slate-50 dark:bg-slate-800 active:bg-slate-100 dark:active:bg-slate-700'">
                  <span class="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                    :class="page.url.startsWith('/admin/users')
                      ? 'bg-rose-100 dark:bg-rose-900'
                      : 'bg-white dark:bg-slate-700 shadow-sm'">
                    <svg class="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8"
                      :class="page.url.startsWith('/admin/users') ? 'text-rose-600 dark:text-rose-400' : 'text-slate-500 dark:text-slate-400'">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"/>
                    </svg>
                  </span>
                  <span class="text-xs font-semibold text-center"
                    :class="page.url.startsWith('/admin/users') ? 'text-rose-600 dark:text-rose-400' : 'text-slate-700 dark:text-slate-300'">
                    Users
                  </span>
                </Link>

                <Link href="/admin/contact_us_form_users" prefetch @click="moreOpen = false"
                  class="flex flex-col items-center gap-2 p-3 rounded-2xl transition active:scale-[0.98]"
                  :class="page.url.startsWith('/admin/contact_us_form_users')
                    ? 'bg-rose-50 dark:bg-rose-950'
                    : 'bg-slate-50 dark:bg-slate-800 active:bg-slate-100 dark:active:bg-slate-700'">
                  <span class="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                    :class="page.url.startsWith('/admin/contact_us_form_users')
                      ? 'bg-rose-100 dark:bg-rose-900'
                      : 'bg-white dark:bg-slate-700 shadow-sm'">
                    <svg class="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8"
                      :class="page.url.startsWith('/admin/contact_us_form_users') ? 'text-rose-600 dark:text-rose-400' : 'text-slate-500 dark:text-slate-400'">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"/>
                    </svg>
                  </span>
                  <span class="text-xs font-semibold text-center"
                    :class="page.url.startsWith('/admin/contact_us_form_users') ? 'text-rose-600 dark:text-rose-400' : 'text-slate-700 dark:text-slate-300'">
                    Contact Forms
                  </span>
                </Link>
              </div>
            </div>
          </template>

          <!-- Logout -->
          <div class="mx-4 mt-4 mb-2">
            <button @click="logout"
              class="w-full flex items-center gap-3 p-3.5 rounded-2xl transition active:scale-[0.98]
                     bg-red-50 dark:bg-red-950 active:bg-red-100 dark:active:bg-red-900">
              <span class="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900 flex items-center justify-center">
                <svg class="w-5 h-5 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0 3 3m-3-3h12.75"/>
                </svg>
              </span>
              <span class="text-sm font-semibold text-red-600 dark:text-red-400">Logout</span>
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { Link, usePage } from '@inertiajs/vue3'
import SidebarLink from '@/components/SidebarLink.vue'

const props = defineProps({
  businessName: String,
  userEmail: String,
  userInitial: String,
  isSuperAdmin: Boolean,
  logout: Function
})

const emit = defineEmits(['logout'])

const page = usePage()

const isSettingsActive = computed(() => page.url.startsWith('/settings'))
const isReportsActive = computed(() => page.url.startsWith('/reports'))
const isResourcesActive = computed(() => page.url.startsWith('/cities'))
const isAdminActive = computed(() => page.url.startsWith('/admin'))

// Only open submenu sections if user is on that section
const reportsOpen = ref(isReportsActive.value)
const resourcesOpen = ref(isResourcesActive.value)
const adminOpen = ref(isAdminActive.value)
const moreOpen = ref(false)
const isMoreActive = computed(() =>
  page.url.startsWith('/reports') ||
  page.url.startsWith('/settings') ||
  page.url.startsWith('/admin') ||
  page.url.startsWith('/banks') ||
  page.url.startsWith('/wealth-assets') ||
  page.url.startsWith('/cities')
)

function isMobileActive(href) {
  return page.url.startsWith(href)
}

function logout() {
  emit('logout')
}

// Personal Mode Navigation Items
const navItems = [
  {
    href: '/khata',
    label: 'Khata',
    icon: `<path stroke-linecap="round" stroke-linejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"/>`,
  },
  {
    href: '/customers',
    label: 'Customers',
    icon: `<path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"/>`,
  },
  {
    href: '/banks',
    label: 'Banks',
    icon: `<path stroke-linecap="round" stroke-linejoin="round" d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0012 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z"/>`,
  },
  {
    href: '/expenses',
    label: 'Expenses',
    icon: `<path stroke-linecap="round" stroke-linejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z"/>`,
  },
  {
    href: '/incomes',
    label: 'Incomes',
    icon: `<path stroke-linecap="round" stroke-linejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z"/>`,
  },
  {
    href: '/wealth-assets',
    label: 'Assets',
    icon: `<path stroke-linecap="round" stroke-linejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z"/>`,
  },
]

// Mobile bottom nav - Primary tabs (showing in bottom bar)
const mobileNavItems = [
  {
    href: '/khata',
    label: 'Khata',
    icon: `<path stroke-linecap="round" stroke-linejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"/>`,
  },
  {
    href: '/expenses',
    label: 'Expenses',
    icon: `<path stroke-linecap="round" stroke-linejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z"/>`,
  },
  {
    href: '/incomes',
    label: 'Incomes',
    icon: `<path stroke-linecap="round" stroke-linejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z"/>`,
  },
  {
    href: '/customers',
    label: 'Customers',
    icon: `<path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"/>`,
  },
]

// Mobile secondary nav - items shown in "More" sheet
const mobileSecondaryItems = [
  {
    href: '/banks',
    label: 'Banks',
    icon: `<path stroke-linecap="round" stroke-linejoin="round" d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0012 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z"/>`,
  },
  {
    href: '/wealth-assets',
    label: 'Assets',
    icon: `<path stroke-linecap="round" stroke-linejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z"/>`,
  },
  {
    href: '/cities',
    label: 'Cities',
    icon: `<path stroke-linecap="round" stroke-linejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21"/>`,
  },
]

const reportSubItems = [
  { href: '/reports/cashflow', label: 'Cash Flow', icon: `<path stroke-linecap="round" stroke-linejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z"/>` },
  { href: '/reports/wealth', label: 'Wealth Statement', icon: `<path stroke-linecap="round" stroke-linejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941"/>` },
]
</script>
